/**
 * Kayıt servisi — personel giriş/çıkış kayıtları.
 * Firestore 'kayitlar' koleksiyonu.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { GpsKonumu } from '@/lib/gps'

const KAYITLAR = 'kayitlar'

export type KayitTipi = 'giris' | 'cikis'
/** 'otomatik' = sistem (autoCheckoutCron) tarafından yazılan çıkış. */
export type KayitYontemi = 'qr' | 'manuelSorumlu' | 'manuelBoss' | 'otomatik'

export interface Kayit {
  id: string
  personelId: string
  subeId: string
  zaman: Timestamp
  tip: KayitTipi
  yontem: KayitYontemi
  gpsLat?: number
  gpsLng?: number
  gpsAccuracy?: number
  clientTime?: number
  selfieUrl?: string
  notlar?: string
}

function yeniKayitId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, KAYITLAR)).id
}

export async function personelKayitlariGetir(
  personelId: string,
  limit?: number
): Promise<Kayit[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, KAYITLAR), where('personelId', '==', personelId))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
  liste.sort((a, b) => b.zaman.toMillis() - a.zaman.toMillis())
  return limit ? liste.slice(0, limit) : liste
}

export async function sonKayitGetir(personelId: string): Promise<Kayit | null> {
  const liste = await personelKayitlariGetir(personelId, 1)
  return liste[0] ?? null
}

export async function icerideMi(personelId: string): Promise<boolean> {
  const son = await sonKayitGetir(personelId)
  return son?.tip === 'giris'
}

interface KayitOlusturmaData {
  personelId: string
  subeId: string
  tip: KayitTipi
  yontem: KayitYontemi
  konum?: GpsKonumu
  selfieUrl?: string
  notlar?: string
}

export async function kayitOlustur(data: KayitOlusturmaData): Promise<string> {
  const db = getFirestoreDb()
  const id = yeniKayitId()

  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    subeId: data.subeId,
    tip: data.tip,
    yontem: data.yontem,
    zaman: nowServer(),
    clientTime: Date.now(),
  }

  if (data.konum) {
    docData.gpsLat = data.konum.lat
    docData.gpsLng = data.konum.lng
    if (data.konum.accuracy !== undefined) {
      docData.gpsAccuracy = data.konum.accuracy
    }
  }
  if (data.selfieUrl) docData.selfieUrl = data.selfieUrl
  if (data.notlar) docData.notlar = data.notlar

  await setDoc(doc(db, KAYITLAR, id), docData)
  return id
}

/**
 * Manuel kayıt — BOSS veya Sorumlu tarafından geçmiş bir tarih/saat için oluşturulur.
 * QR ile farklı: özel zaman değeri verilir, GPS yok.
 */
export async function manuelKayitOlustur(data: {
  personelId: string
  subeId: string
  tip: KayitTipi
  zaman: Date
  yontem: KayitYontemi  // 'manuelBoss' veya 'manuelSorumlu'
  notlar?: string
  selfieUrl?: string
}): Promise<string> {
  const db = getFirestoreDb()
  const id = yeniKayitId()

  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    subeId: data.subeId,
    tip: data.tip,
    yontem: data.yontem,
    zaman: Timestamp.fromDate(data.zaman),
    clientTime: Date.now(),
  }
  if (data.notlar) docData.notlar = data.notlar
  if (data.selfieUrl) docData.selfieUrl = data.selfieUrl

  await setDoc(doc(db, KAYITLAR, id), docData)
  return id
}

export async function bugunkuKayitlar(personelId: string): Promise<Kayit[]> {
  const tum = await personelKayitlariGetir(personelId)
  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)
  const yarin = new Date(bugun)
  yarin.setDate(yarin.getDate() + 1)

  return tum.filter((k) => {
    const zaman = k.zaman.toDate()
    return zaman >= bugun && zaman < yarin
  })
}

export function bugunDakika(kayitlar: Kayit[]): number {
  const sirali = [...kayitlar].sort(
    (a, b) => a.zaman.toMillis() - b.zaman.toMillis()
  )

  let toplamMs = 0
  let aktifGiris: Kayit | null = null

  for (const k of sirali) {
    if (k.tip === 'giris') {
      aktifGiris = k
    } else if (k.tip === 'cikis' && aktifGiris) {
      toplamMs += k.zaman.toMillis() - aktifGiris.zaman.toMillis()
      aktifGiris = null
    }
  }

  if (aktifGiris) {
    toplamMs += Date.now() - aktifGiris.zaman.toMillis()
  }

  return Math.floor(toplamMs / 60000)
}

export async function bugunkuTumKayitlar(): Promise<Kayit[]> {
  const db = getFirestoreDb()
  const baslangic = new Date()
  baslangic.setHours(0, 0, 0, 0)
  const ts = Timestamp.fromDate(baslangic)

  const q = query(collection(db, KAYITLAR), where('zaman', '>=', ts))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
  liste.sort((a, b) => b.zaman.toMillis() - a.zaman.toMillis())
  return liste
}

export function sonKayitHaritasi(kayitlar: Kayit[]): Map<string, Kayit> {
  const harita = new Map<string, Kayit>()
  for (const k of kayitlar) {
    const mevcut = harita.get(k.personelId)
    if (!mevcut || k.zaman.toMillis() > mevcut.zaman.toMillis()) {
      harita.set(k.personelId, k)
    }
  }
  return harita
}

/** Tek bir kaydı getir (düzenleme/silme öncesi). */
export async function kayitGetir(id: string): Promise<Kayit | null> {
  const db = getFirestoreDb()
  const s = await getDoc(doc(db, KAYITLAR, id))
  if (!s.exists()) return null
  return { id: s.id, ...s.data() } as Kayit
}

/**
 * Bir kaydın saatini (zaman) düzenle — BOSS.
 * Gelecek tarihli zamana izin verilmez.
 */
export async function kayitZamaniGuncelle(id: string, yeniZaman: Date): Promise<void> {
  if (yeniZaman.getTime() > Date.now()) {
    throw new Error('Gelecek tarihli kayıt olamaz.')
  }
  const db = getFirestoreDb()
  await updateDoc(doc(db, KAYITLAR, id), {
    zaman: Timestamp.fromDate(yeniZaman),
    duzenlendi: true,
    duzenlenmeTarihi: nowServer(),
  })
}

/** Bir kaydı kalıcı sil — BOSS. */
export async function kayitSil(id: string): Promise<void> {
  const db = getFirestoreDb()
  await deleteDoc(doc(db, KAYITLAR, id))
}
